/**
 * Pair-proposal API client + force-pair / pair-edit helpers.
 *
 *   GET    /api/pair-proposals
 *   GET    /api/pair-proposals/:slug
 *   POST   /api/pair-proposals/:slug/approve
 *   POST   /api/pair-proposals/:slug/reject
 *   POST   /api/account-pairs/force-pair
 *   POST   /api/account-pairs/:slug/dissolve
 *   POST   /api/account-pairs/:slug/swap-leg
 *   POST   /api/account-pairs/:slug/validate-symbol
 */

import type {
  ForcePairReq,
  PairProposalListItem,
  SwapLegReq,
} from "@pfh/shared";
import { api } from "../api";

export type { PairProposalListItem, PairDeltaEntry } from "@pfh/shared";

export async function listPairProposals(): Promise<PairProposalListItem[]> {
  const res = await api.get<{ proposals: PairProposalListItem[] }>(
    "/api/pair-proposals",
  );
  return res.proposals;
}

export async function getPairProposal(slug: string): Promise<PairProposalListItem> {
  const res = await api.get<{ proposal: PairProposalListItem }>(
    `/api/pair-proposals/${encodeURIComponent(slug)}`,
  );
  return res.proposal;
}

export async function approvePairProposal(
  slug: string,
  reason?: string,
): Promise<{ ok: true; result: { accountPairSlug: string | null } }> {
  return api.post(
    `/api/pair-proposals/${encodeURIComponent(slug)}/approve`,
    { reason },
  );
}

export async function rejectPairProposal(slug: string, reason?: string): Promise<void> {
  await api.post(`/api/pair-proposals/${encodeURIComponent(slug)}/reject`, { reason });
}

export async function forcePair(body: ForcePairReq): Promise<{
  ok: true;
  result: {
    paired: boolean;
    pairId: string | null;
    pairSlug: string | null;
    accountPairId: string | null;
    accountPairSlug: string | null;
  };
}> {
  return api.post("/api/account-pairs/force-pair", body);
}

export interface DissolveResult {
  long: { paired: boolean; accountPairSlug: string | null; reason: string | null } | null;
  short: { paired: boolean; accountPairSlug: string | null; reason: string | null } | null;
}

export async function dissolveAccountPair(slug: string): Promise<DissolveResult> {
  const res = await api.post<{
    ok: true;
    rematches?: {
      long: { paired: boolean; accountPairSlug: string | null; reason: string | null } | null;
      short: { paired: boolean; accountPairSlug: string | null; reason: string | null } | null;
    };
  }>(`/api/account-pairs/${encodeURIComponent(slug)}/dissolve`, {});
  return {
    long: res.rematches?.long ?? null,
    short: res.rematches?.short ?? null,
  };
}

export async function swapLeg(slug: string, body: SwapLegReq): Promise<{
  ok: true;
  quality: "perfect" | "near" | "mismatch" | "operator_forced";
  deltas: unknown[];
}> {
  return api.post(
    `/api/account-pairs/${encodeURIComponent(slug)}/swap-leg`,
    body,
  );
}

export interface ValidateSymbolResult {
  ok: boolean;
  long: {
    status: "pending" | "resolved" | "timeout" | "missing" | "error";
    ok: boolean;
    resolvedSymbol?: string | null;
    leverage?: number | null;
    contractSize?: number | null;
    minLot?: number | null;
    maxLot?: number | null;
    spreadPips?: number | null;
    error?: string | null;
  };
  short: ValidateSymbolResult["long"];
  safeLots: number | null;
}

export async function validateSymbol(
  slug: string,
  symbol: string,
): Promise<ValidateSymbolResult> {
  return api.post(
    `/api/account-pairs/${encodeURIComponent(slug)}/validate-symbol`,
    { symbol },
  );
}
